import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { 
  Users, TrendingUp, Target, UserX, Mail, Phone, RefreshCw,
  ArrowLeft, MessageCircle, Calendar, Download, FileDown, Share2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RetargetingDashboard = () => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [abandonedLeads, setAbandonedLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDays, setSelectedDays] = useState(7);
  const [exportLoading, setExportLoading] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [selectedDays]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [analyticsRes, leadsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/tracking/analytics?days=${selectedDays}`),
        fetch(`${BACKEND_URL}/api/tracking/abandoned-leads?days=${selectedDays}&limit=50`)
      ]);
      if (analyticsRes.ok) setAnalytics(await analyticsRes.json());
      if (leadsRes.ok) {
        const data = await leadsRes.json();
        setAbandonedLeads(data.leads || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async (audienceType) => {
    setExportLoading(true);
    try {
      const url = `${BACKEND_URL}/api/tracking/export-audiences?days=${selectedDays}&audience_type=${audienceType}`;
      const res = await fetch(url);
      if (res.ok) {
        const blob = await res.blob();
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `custom_audience_${audienceType}_${selectedDays}d.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
      }
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setExportLoading(false);
    }
  };

  const generateWhatsAppLink = (lead) => {
    const phone = (lead.phone || '').replace(/[^0-9]/g, '');
    const phoneNumber = phone.startsWith('91') ? phone : `91${phone}`;
    const message = encodeURIComponent(
      `Hi ${lead.name || 'there'}!\n\nI noticed you were interested in our virtual CFO services. As a special offer, you can still use code SUCCESS20 for 20% off!\n\nWould you like to schedule a quick call?\n\nBest,\nNSRM & Associates`
    );
    return `https://wa.me/${phoneNumber}?text=${message}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/admin/dashboard')} className="shrink-0">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900" data-testid="retargeting-title">Retargeting Dashboard</h1>
              <p className="text-gray-600">Track, retarget & export audiences for Facebook/Instagram ads</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <select value={selectedDays} onChange={(e) => setSelectedDays(Number(e.target.value))} className="border rounded-lg px-3 py-2">
              <option value={1}>Last 24 hours</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <Button onClick={fetchData} variant="outline" disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>

        {/* Analytics Cards */}
        {analytics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            {[
              { icon: Users, color: 'blue', value: analytics.total_visitors, label: 'Total Visitors' },
              { icon: Target, color: 'purple', value: analytics.form_openers, label: 'Form Opens' },
              { icon: TrendingUp, color: 'green', value: analytics.conversions, label: 'Conversions' },
              { icon: UserX, color: 'red', value: analytics.abandoned, label: 'Abandoned' },
              { icon: Mail, color: 'amber', value: analytics.leads_captured, label: 'Leads Captured' },
              { icon: TrendingUp, color: 'teal', value: `${analytics.conversion_rate}%`, label: 'Conv. Rate' }
            ].map((stat, i) => (
              <Card key={c.type || c.title || `card-${i}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 bg-${stat.color}-100 rounded-lg`}>
                      <stat.icon className={`w-5 h-5 text-${stat.color}-600`} />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{stat.value}</p>
                      <p className="text-xs text-gray-500">{stat.label}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Export Section for FB/IG Ads */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-blue-600" />
              Export for Facebook / Instagram Ads
            </CardTitle>
            <CardDescription>
              Download CSV files to create Custom Audiences in Meta Ads Manager. Upload at{' '}
              <a href="https://business.facebook.com/adsmanager/audiences" target="_blank" rel="noopener noreferrer"
                className="text-blue-600 underline">Meta Ads Manager &rarr; Audiences &rarr; Create Custom Audience</a>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { type: 'all', title: 'All Visitors', desc: 'Everyone who visited your site', icon: Users, color: 'blue' },
                { type: 'abandoned', title: 'Abandoned Leads', desc: 'Opened form but didn\'t book', icon: UserX, color: 'red' },
                { type: 'leads', title: 'Captured Leads', desc: 'Left email or phone', icon: Mail, color: 'amber' },
                { type: 'visitors', title: 'Engaged Visitors', desc: '2+ page views', icon: TrendingUp, color: 'green' }
              ].map((audience) => (
                <div key={audience.type} className="border rounded-xl p-4 hover:border-blue-300 transition-colors" data-testid={`export-${audience.type}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <audience.icon className={`w-5 h-5 text-${audience.color}-600`} />
                    <h4 className="font-semibold text-gray-900">{audience.title}</h4>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{audience.desc}</p>
                  <Button
                    onClick={() => handleExportCSV(audience.type)}
                    disabled={exportLoading}
                    variant="outline"
                    size="sm"
                    className="w-full"
                    data-testid={`export-btn-${audience.type}`}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {exportLoading ? 'Exporting...' : 'Download CSV'}
                  </Button>
                </div>
              ))}
            </div>

            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <h4 className="font-semibold text-blue-900 mb-2">How to use with Facebook/Instagram Ads:</h4>
              <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                <li>Download the CSV for your target audience above</li>
                <li>Go to <strong>Meta Business Suite &rarr; Audiences</strong></li>
                <li>Click <strong>Create Audience &rarr; Custom Audience &rarr; Customer List</strong></li>
                <li>Upload the CSV file and map the columns (email, phone, name)</li>
                <li>Create a <strong>Lookalike Audience</strong> from this list for broader reach</li>
                <li>Use this audience in your Facebook & Instagram ad campaigns</li>
              </ol>
            </div>
          </CardContent>
        </Card>

        {/* Abandoned Leads Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserX className="w-5 h-5 text-red-500" />
              Abandoned Leads - Ready for Retargeting
            </CardTitle>
            <CardDescription>
              Visitors who opened the booking form but didn't complete - contact them to convert!
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : abandonedLeads.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <UserX className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No abandoned leads found in this period</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 font-medium text-gray-600">Contact</th>
                      <th className="text-left p-3 font-medium text-gray-600">Source</th>
                      <th className="text-left p-3 font-medium text-gray-600">Activity</th>
                      <th className="text-left p-3 font-medium text-gray-600">Last Visit</th>
                      <th className="text-left p-3 font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {abandonedLeads.map((lead, index) => (
                      <tr key={lead.email || `lead-${index}`} className="border-b hover:bg-gray-50" data-testid={`lead-row-${index}`}>
                        <td className="p-3">
                          {lead.name && <p className="font-medium text-gray-900">{lead.name}</p>}
                          {lead.email && <p className="text-sm text-gray-600 flex items-center gap-1"><Mail className="w-3 h-3" />{lead.email}</p>}
                          {lead.phone && <p className="text-sm text-gray-600 flex items-center gap-1"><Phone className="w-3 h-3" />{lead.phone}</p>}
                        </td>
                        <td className="p-3 text-sm">
                          {lead.utm_source ? (
                            <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs">{lead.utm_source}</span>
                          ) : lead.referrer ? (
                            <span className="text-gray-500 text-xs truncate max-w-[120px] block">{lead.referrer}</span>
                          ) : (
                            <span className="text-gray-400 text-xs">Direct</span>
                          )}
                        </td>
                        <td className="p-3 text-sm">
                          <p>{lead.page_views} views</p>
                          <p className="text-amber-600">{lead.form_opens} opens</p>
                          {lead.form_abandons > 0 && <p className="text-red-500">{lead.form_abandons} abandons</p>}
                        </td>
                        <td className="p-3 text-sm">
                          <p className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(lead.last_visit)}</p>
                        </td>
                        <td className="p-3">
                          <div className="flex gap-2">
                            {lead.phone && (
                              <a href={generateWhatsAppLink(lead)} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 bg-green-500 hover:bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors">
                                <MessageCircle className="w-4 h-4" /> WhatsApp
                              </a>
                            )}
                            {lead.email && (
                              <a href={`mailto:${lead.email}?subject=Complete Your Consultation Booking&body=Hi ${lead.name || ''},%0D%0A%0D%0AI noticed you were interested in our virtual CFO services. Use code SUCCESS20 for 20%25 off!%0D%0A%0D%0ABest,%0D%0ANSRM %26 Associates`}
                                className="inline-flex items-center gap-1 bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors">
                                <Mail className="w-4 h-4" /> Email
                              </a>
                            )}
                            {lead.phone && (
                              <a href={`tel:${lead.phone}`}
                                className="inline-flex items-center gap-1 bg-gray-500 hover:bg-gray-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors">
                                <Phone className="w-4 h-4" /> Call
                              </a>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RetargetingDashboard;
